import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    FEATURE_DYNAMIC_REPORTING,
    FEATURE_OUTSTANDING_TASKS_VIEW,
    FEATURE_SITES,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasAnyPermission,
    hasFeatureFlag,
    OrganisationService,
    PersonDto,
    PersonService,
    PersonsOrganisationDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    dashboardSelectionDisplayableLabels,
    findAvailableDefaultDashboardsByOrganisationFeatureFlag
} from '@flyfreely-portal-ui/ui';
import moment from 'moment';
import { firstValueFrom, Subject } from 'rxjs';
import { startWith, takeUntil, tap } from 'rxjs/operators';
import {
    DEFAULT_DASHBOARD,
    OrganisationSettingsService,
    UpdateOrganisationSettingsForm
} from '../organisation-settings.service';

@Component({
    selector: 'organisation-overview',
    templateUrl: './organisation-overview.component.html'
})
export class OrganisationOverview implements OnChanges {
    @Input() organisation: PersonsOrganisationDto;

    owner: PersonDto;

    settingsForm: FormGroup<UpdateOrganisationSettingsForm>;

    dashboardOptions: { value: string; label: string }[] = [];

    canEdit = false;
    canUseSites = false;
    canUseReporting = false;
    canUseTasks = false;

    private ngUnsubscribe$ = new Subject<void>();
    private workTracker = new WorkTracker();
    working = false;

    timeZones = moment.tz.names();

    constructor(
        private personService: PersonService,
        private organisationService: OrganisationService,
        private organisationSettingsService: OrganisationSettingsService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.settingsForm = this.organisationSettingsService.settingsForm;
    }

    ngOnInit() {
        this.refreshAvailableDashboards();
        this.settingsForm.controls.defaultDashboard.valueChanges
            .pipe(
                startWith(this.settingsForm.value.defaultDashboard),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(settings => {
                if (
                    settings == null ||
                    settings == '' ||
                    !this.dashboardOptions.some(d => d.value === settings)
                ) {
                    this.settingsForm.controls.defaultDashboard.patchValue(
                        DEFAULT_DASHBOARD,
                        {
                            emitEvent: false,
                            onlySelf: true
                        }
                    );
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.refreshOwner();
        this.canEdit = hasAnyPermission(
            this.organisation,
            PersonsOrganisationDto.Permissions.ORGANISATION_EDIT
        );
        this.canUseSites = hasFeatureFlag(this.organisation, FEATURE_SITES);
        this.canUseReporting = hasFeatureFlag(
            this.organisation,
            FEATURE_DYNAMIC_REPORTING
        );
        this.canUseTasks = hasFeatureFlag(
            this.organisation,
            FEATURE_OUTSTANDING_TASKS_VIEW
        );
        this.refreshAvailableDashboards();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshAvailableDashboards() {
        // We only want to show options for dashboards the org actually has available based on feature flags
        const allowedOptions =
            findAvailableDefaultDashboardsByOrganisationFeatureFlag(
                this.organisation
            );
        this.dashboardOptions = [
            { value: DEFAULT_DASHBOARD, label: 'Default' },
            ...allowedOptions.map(option => ({
                value: option,
                label: dashboardSelectionDisplayableLabels[option]
            }))
        ];
    }

    private refreshOwner() {
        if (this.organisation == null) {
            this.owner = null;
            return;
        }
        this.personService
            .findPerson(this.organisation.ownerId, this.organisation.id)
            .subscribe({
                next: person => (this.owner = person),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error refreshing organisation owner: ${error.message}`
                    )
            });
    }

    // Done as an arrow function so it can be used as a callback
    updateName = (value: { name: string }) => {
        if (value == null || value.name == null || value.name.length <= 2) {
            return Promise.reject();
        }
        return firstValueFrom(
            this.organisationService
                .updateOrganisationName(this.organisation.id, {
                    name: value.name
                })
                .pipe(
                    takeUntil(this.ngUnsubscribe$),
                    tap({
                        next: () =>
                            this.logging.success('Organisation name updated'),
                        error: (error: FlyFreelyError) =>
                            this.logging.error(
                                error,
                                'Could not update organisation name'
                            )
                    })
                )
        ).finally(this.workTracker.createTracker());
    };

    // Done as an arrow function so it can be used as a callback
    updateTimeZone = (value: { timeZone: string }) => {
        if (
            value == null ||
            value.timeZone == null ||
            value.timeZone.length <= 2
        ) {
            return Promise.reject();
        }
        return firstValueFrom(
            this.organisationService
                .updateOrganisationTimeZone(this.organisation.id, {
                    timeZone: value.timeZone
                })
                .pipe(
                    takeUntil(this.ngUnsubscribe$),
                    tap({
                        next: () =>
                            this.logging.success(
                                'Organisation timezone updated'
                            ),
                        error: (error: FlyFreelyError) =>
                            this.logging.error(
                                error,
                                'Could not update organisation timezone'
                            )
                    })
                )
        ).finally(this.workTracker.createTracker());
    };

    get selectedDefaultDashboard() {
        return this.settingsForm.value.defaultDashboard ?? DEFAULT_DASHBOARD;
    }

    get selectedDefaultDashboardLabel() {
        return this.dashboardOptions.find(
            option => option.value === this.selectedDefaultDashboard
        )?.label;
    }

    // Done as an arrow function so it can be used as a callback
    updateDefaultDashboard = (value: { defaultDashboard: string }) => {
        const control = this.settingsForm.controls.defaultDashboard;
        const previous = control.value;
        control.patchValue(value?.defaultDashboard ?? DEFAULT_DASHBOARD, {
            emitEvent: false
        });
        return this.organisationSettingsService
            .updateSettings()
            .catch(() => {
                control.patchValue(previous, { emitEvent: false });
                return Promise.reject(
                    'Could not update the default dashboard'
                ) as Promise<void>;
            })
            .finally(this.workTracker.createTracker());
    };
}
